import React from 'react';
import { Link } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { NewTabHint, arrowGlyph, newTabRel } from './linkParts';

/** Hover colour: rust on light surfaces; peach on navy (rust is only 2.52:1 there). */
export const TEXT_LINK_TONES = {
  default: 'hover:text-brand-rust',
  inverse: 'hover:text-brand-peach',
};

/** md = 14px → 15px from the nav breakpoint; sm = 12px. */
export const TEXT_LINK_SIZES = {
  md: 'text-sm nav:text-[0.9375rem]',
  sm: 'text-xs',
};

/**
 * The underlined inline action ("Read the Tappd story →", "Ask us anything ↗"): DM Sans
 * semibold, a 1px underline in the current colour, rust (or peach) on hover. Renders a router `Link`
 * for `to`, an `<a>` for `href`, otherwise a `<button type="button">` that looks the same.
 *
 * Props:
 * - `to` | `href` | neither (button; pass `onClick`)
 * - `arrow`: false (default) | true = append a decorative arrow (aria-hidden) chosen by the
 *   destination, as Button's: " →" for same-site navigation and in-page actions (`to`, a
 *   same-origin `href`, a button), " ↗" only for what opens outside the site (another
 *   origin, `mailto:`, maps), none for `tel:` / `sms:` | a glyph that overrides the rule
 * - `tone`: 'default' (light surfaces) | 'inverse' (navy surfaces)
 * - `size`: 'md' (default) | 'sm'
 * - `target`, `rel`, `newTabLabel`: as Button (new tabs get noopener noreferrer + sr text)
 * - `className`, `children`, `ref` and other props pass through.
 */
const TextLink = React.forwardRef(function TextLink(
  { to, href, arrow = false, tone = 'default', size = 'md', target, rel, newTabLabel, className, children, ...rest },
  ref,
) {
  const glyph = arrowGlyph(arrow, { to, href });
  const classes = cn(
    'inline-block cursor-pointer border-b border-current bg-transparent px-0 py-[7px] text-left font-body font-semibold no-underline transition-colors duration-180 ease-out',
    TEXT_LINK_SIZES[size] || TEXT_LINK_SIZES.md,
    'leading-normal', // after the size: a later text-[size] would drop it in cn()
    TEXT_LINK_TONES[tone] || TEXT_LINK_TONES.default,
    className,
  );
  const content = (
    <>
      {children}
      {glyph !== null ? <span aria-hidden="true">{'\u00a0'}{glyph}</span> : null}
    </>
  );
  if (to !== undefined) {
    return (
      <Link ref={ref} to={to} target={target} rel={newTabRel(target, rel)} className={classes} {...rest}>
        {content}
        <NewTabHint target={target} label={newTabLabel} />
      </Link>
    );
  }
  if (href !== undefined) {
    return (
      <a ref={ref} href={href} target={target} rel={newTabRel(target, rel)} className={classes} {...rest}>
        {content}
        <NewTabHint target={target} label={newTabLabel} />
      </a>
    );
  }
  return (
    <button ref={ref} type="button" className={classes} {...rest}>
      {content}
    </button>
  );
});

export default TextLink;
